// @ts-nocheck
// features/chat/useChat.ts
// ✅ v4 — Fixed: loadConversationById now clears messages before loading,
//          handleWebhookSearch supports webhook responses that come back as an array (mobile fix).

import { useReducer, useRef, useCallback, useEffect } from 'react';
import { Message, Course, SearchData, LeadGateData, ParsedQuizContext } from '@/types/chat';
import { chatService } from './chatService';
import { parseMessage } from './messageParser';
import { conversationService } from '@/utils/conversationService';
import { supabase } from '@/utils/supabase';
import { trackChatStart, trackChatSessionStart, trackButtonClick, trackChatFlowEvent } from '@/utils/analytics';

let messageIdCounter = 0;
let lastTimestamp = 0;

function generateUniqueMessageId(): string {
  const now = Date.now();
  if (now === lastTimestamp) {
    messageIdCounter += 1;
  } else {
    lastTimestamp = now;
    messageIdCounter = 0;
  }
  const randomPart = Math.random().toString(36).substring(2, 11);
  const performancePart =
    typeof performance !== 'undefined' && performance.now
      ? Math.floor(performance.now() * 1000).toString(36)
      : Math.random().toString(36).substring(2, 6);
  return `${now}-${messageIdCounter}-${randomPart}-${performancePart}`;
}

function limitOptions(options: string[], maxOptions: number = 4): string[] {
  if (options.length <= maxOptions) return options;
  if (__DEV__) console.warn(`[limitOptions] ⚠️ Too many options (${options.length}), limiting to ${maxOptions}`);
  return options.slice(0, maxOptions);
}

function getSmartQuickReplies(shownInsights: Set<string>): string[] {
  const allOptions = [
    { key: 'comparison', text: 'תסכם לי את ההבדלים' },
    { key: 'bottomLine', text: 'בשורה התחתונה - מה הכי מתאים לי?' },
    { key: 'analysis', text: 'עזור לי לבחור' },
  ];
  const availableOptions = allOptions.filter((option) => !shownInsights.has(option.key));
  if (availableOptions.length === 0) return ['אפשר כיוון אחר?'];
  return limitOptions(availableOptions.map((opt) => opt.text), 2);
}

function normalizeSupabaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function safeParseJson<T = any>(text: string): T | null {
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

interface WebhookResult {
  matchCount: number;
  userSummary: string;
  courses: Course[];
}

function validateWebhookResult(raw: any): WebhookResult {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid webhook response: not an object');
  const matchCount = typeof raw.matchCount === 'number' && raw.matchCount >= 0 ? raw.matchCount : 0;
  const userSummary = typeof raw.userSummary === 'string' ? raw.userSummary : '';
  const courses = Array.isArray(raw.courses)
    ? raw.courses.filter((c: any) => c && typeof c === 'object' && c.id)
    : [];
  return { matchCount, userSummary, courses };
}

// ✅ FIX: webhook sometimes returns: [{ content, courses }]
// Normalize to: { matchCount, userSummary, courses }
function normalizeWebhookResponse(raw: any): any {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (first && typeof first === 'object') {
      const courses = Array.isArray((first as any).courses) ? (first as any).courses : [];
      const content = typeof (first as any).content === 'string' ? (first as any).content : '';
      return {
        matchCount: courses.length,
        userSummary: content,
        courses,
      };
    }
    return { matchCount: 0, userSummary: '', courses: [] };
  }
  return raw;
}

function safeDecodeURIComponent(raw: string): string {
  try {
    return /%[0-9A-Fa-f]{2}/.test(raw) ? decodeURIComponent(raw) : raw;
  } catch {
    return raw;
  }
}

type AnySearchData = SearchData & {
  summary?: string;
  topic?: string;
  format?: string;
  level?: string;
  audience?: string;
  type?: string;
  source?: string;
  leadCaptured?: boolean;
  skip_lead_gate?: boolean;
  lead_full_name?: string;
  lead_phone?: string;
};

function extractSearchDataFromText(raw: string): AnySearchData | null {
  const text = (raw ?? '').toString().trim();
  if (!text) return null;

  if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
    const parsed = safeParseJson<any>(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if ('__proto__' in parsed || 'constructor' in parsed || 'prototype' in parsed) {
        console.warn('[extractSearchDataFromText] ⚠️ Blocked potentially malicious JSON');
        return null;
      }
      const candidate: AnySearchData = parsed;
      if (candidate.topic || candidate.summary || (candidate as any).query_type) return candidate;
    }
  }

  const lines = text.split('\n');
  const out: Record<string, any> = {};
  for (const line of lines) {
    const m = line.match(/^\s*([a-zA-Z_]+)\s*:\s*(.*)\s*$/);
    if (!m) continue;
    const k = m[1].trim();
    const v = (m[2] ?? '').toString().trim();
    if (!k) continue;
    if (
      [
        'summary',
        'topic',
        'format',
        'level',
        'audience',
        'type',
        'source',
        'leadCaptured',
        'skip_lead_gate',
        'lead_full_name',
        'lead_phone',
        'query_type',
        'mentor_name',
      ].includes(k)
    ) {
      out[k] = v;
    }
  }

  if (Object.keys(out).length === 0) return null;
  const candidate: AnySearchData = out as any;
  if (!candidate.summary && candidate.topic) candidate.summary = `מחפש קורס בתחום ${candidate.topic}`;
  if (candidate.topic || candidate.summary || (candidate as any).query_type) return candidate;
  return null;
}

function shouldSkipLeadGate(searchData: SearchData): boolean {
  const sd = searchData as any;
  if (sd?.source === 'quiz') return true;
  if (sd?.skip_lead_gate === true) return true;
  if (sd?.leadCaptured === true) return true;
  return false;
}

// ─── Quiz warm intro builder ──────────────────────────────────────────────────

const TRACK_EMOJI: Record<string, string> = {
  programming: '💻',
  data_ai: '🧠',
  digital_marketing: '📣',
  ui_ux: '🎨',
  stock_market: '📈',
  psychometric: '📝',
  business_management: '💼',
  content_creation: '🎬',
  customer_service: '🤝',
};

function buildQuizWarmIntro(ctx: ParsedQuizContext): string {
  const emoji = TRACK_EMOJI[ctx.trackKey] ?? '🎯';
  const level = (ctx.levels?.best ?? 'גבוהה').toString().trim();
  const lines: string[] = [`שלום! ראיתי את תוצאות השאלון שלך ${emoji}`];

  if (ctx.trackTitle?.trim()) {
    lines.push(`יש לך התאמה ${level} למסלול ${ctx.trackTitle} — בוא נמצא לך קורס שבאמת מתאים לך.`);
  } else {
    lines.push(`יש לך התאמה ${level} למסלול שבחרת — בוא נמצא לך קורס שבאמת מתאים לך.`);
  }

  lines.push(`מה הכי חשוב לך עכשיו: מחיר, פורמט לימוד, או יעד קריירה?`);

  return lines.join('\n');
}

function buildQuizSearchData(ctx: ParsedQuizContext): AnySearchData {
  return {
    topic: ctx.trackKey.replace(/_/g, ' '),
    summary: `קורס ${ctx.trackTitle}`,
    source: 'quiz',
    skip_lead_gate: true,
    leadCaptured: ctx.leadCaptured,
  };
}

const RESUME_LEAD_GATE_TEXT = 'המשך השארת פרטים';
const SEARCH_TIMEOUT_MS = 30_000;
const RECOVERY_QUICK_REPLIES = ['נסה שוב עכשיו', 'נדייק את החיפוש יחד', 'אני רוצה להשאיר פרטים'];

// ─── State types ──────────────────────────────────────────────────────────────

export interface ChatState {
  messages: Message[];
  courses: Course[];
  isLoading: boolean;
  isSending: boolean;
  isSearching: boolean;
  searchingMessage: string | null;
  quickReplies: string[];
  lastSearchData: SearchData | null;
  lastDisplayedCourses: Course[];
  displayedCourseIds: Set<string>;
  coursesMessageId: string | null;
  skipTypingIds: Set<string>;
  placeholderText: string;
  hasUserInteracted: boolean;
  shouldAutoScroll: boolean;
  waitingForRefinement: boolean;
  recommendedCourse: Course | null;
  shownInsights: Set<string>;
  leadGateData: LeadGateData | null;
  isLeadGateLocked: boolean;
  currentConversationId: string | null;
  userMessageCount: number;
  isMessageLimitReached: boolean;
  messageBlockEmailCaptured: boolean;
}

type ChatAction =
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'ADD_MESSAGES'; payload: Message[] }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SENDING'; payload: boolean }
  | { type: 'SET_SEARCHING'; payload: { isSearching: boolean; message?: string | null } }
  | { type: 'SET_COURSES'; payload: { courses: Course[]; messageId: string } }
  | { type: 'CLEAR_COURSES' }
  | { type: 'SET_QUICK_REPLIES'; payload: string[] }
  | { type: 'CLEAR_QUICK_REPLIES' }
  | { type: 'SET_LAST_SEARCH_DATA'; payload: SearchData | null }
  | { type: 'SET_PLACEHOLDER'; payload: string }
  | { type: 'SKIP_TYPING'; payload: string }
  | { type: 'SET_USER_INTERACTED'; payload: boolean }
  | { type: 'SET_AUTO_SCROLL'; payload: boolean }
  | { type: 'SET_WAITING_FOR_REFINEMENT'; payload: boolean }
  | { type: 'SET_RECOMMENDED_COURSE'; payload: Course | null }
  | { type: 'ADD_SHOWN_INSIGHT'; payload: string }
  | { type: 'SET_LEAD_GATE_DATA'; payload: LeadGateData | null }
  | { type: 'SET_LEAD_GATE_LOCKED'; payload: boolean }
  | { type: 'SET_CONVERSATION_ID'; payload: string | null }
  | { type: 'INCREMENT_USER_MESSAGE_COUNT' }
  | { type: 'SET_MESSAGE_BLOCK_EMAIL_CAPTURED'; payload: boolean };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      if (action.payload.role === 'assistant') {
        conversationService.saveMessage('assistant', action.payload.content).catch((err) => {
          console.error('[REDUCER] ⚠️ Failed to save assistant message:', err);
        });
      }
      return { ...state, messages: [...state.messages, action.payload] };

    case 'ADD_MESSAGES':
      return { ...state, messages: [...state.messages, ...action.payload] };

    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_SENDING':
      return { ...state, isSending: action.payload };

    case 'SET_SEARCHING':
      return {
        ...state,
        isSearching: action.payload.isSearching,
        searchingMessage: action.payload.message !== undefined ? action.payload.message : state.searchingMessage,
      };

    case 'SET_COURSES': {
      const newCourseIds = new Set(state.displayedCourseIds);
      action.payload.courses.forEach((course) => {
        if (course.id) newCourseIds.add(course.id);
      });
      return {
        ...state,
        courses: action.payload.courses,
        lastDisplayedCourses: action.payload.courses,
        displayedCourseIds: newCourseIds,
        coursesMessageId: action.payload.messageId,
      };
    }

    case 'CLEAR_COURSES':
      return { ...state, courses: [], lastDisplayedCourses: [], coursesMessageId: null };

    case 'SET_QUICK_REPLIES':
      return { ...state, quickReplies: action.payload };

    case 'CLEAR_QUICK_REPLIES':
      return { ...state, quickReplies: [] };

    case 'SET_LAST_SEARCH_DATA':
      return { ...state, lastSearchData: action.payload };

    case 'SET_PLACEHOLDER':
      return { ...state, placeholderText: action.payload };

    case 'SKIP_TYPING':
      return { ...state, skipTypingIds: new Set([...state.skipTypingIds, action.payload]) };

    case 'SET_USER_INTERACTED':
      return { ...state, hasUserInteracted: action.payload };

    case 'SET_AUTO_SCROLL':
      return { ...state, shouldAutoScroll: action.payload };

    case 'SET_WAITING_FOR_REFINEMENT':
      return { ...state, waitingForRefinement: action.payload };

    case 'SET_RECOMMENDED_COURSE':
      return { ...state, recommendedCourse: action.payload };

    case 'ADD_SHOWN_INSIGHT': {
      const newShownInsights = new Set(state.shownInsights);
      newShownInsights.add(action.payload);
      return { ...state, shownInsights: newShownInsights };
    }

    case 'SET_LEAD_GATE_DATA':
      return {
        ...state,
        leadGateData: action.payload,
        isLeadGateLocked: action.payload !== null,
      };

    case 'SET_LEAD_GATE_LOCKED':
      return { ...state, isLeadGateLocked: action.payload };

    case 'SET_CONVERSATION_ID':
      return { ...state, currentConversationId: action.payload };

    case 'INCREMENT_USER_MESSAGE_COUNT': {
      const newCount = state.userMessageCount + 1;
      return {
        ...state,
        userMessageCount: newCount,
        isMessageLimitReached: newCount >= 8 && !state.messageBlockEmailCaptured,
      };
    }

    case 'SET_MESSAGE_BLOCK_EMAIL_CAPTURED':
      return {
        ...state,
        messageBlockEmailCaptured: action.payload,
        isMessageLimitReached: false,
      };

    default:
      return state;
  }
}

const initialState: ChatState = {
  messages: [],
  courses: [],
  isLoading: false,
  isSending: false,
  isSearching: false,
  searchingMessage: null,
  quickReplies: [],
  lastSearchData: null,
  lastDisplayedCourses: [],
  displayedCourseIds: new Set<string>(),
  coursesMessageId: null,
  skipTypingIds: new Set<string>(),
  placeholderText: 'שלח הודעה',
  hasUserInteracted: false,
  shouldAutoScroll: false,
  waitingForRefinement: false,
  recommendedCourse: null,
  shownInsights: new Set<string>(),
  leadGateData: null,
  isLeadGateLocked: false,
  currentConversationId: null,
  userMessageCount: 0,
  isMessageLimitReached: false,
  messageBlockEmailCaptured: false,
};

export interface UseChatReturn {
  state: ChatState;
  sendMessage: (text: string) => Promise<void>;
  handleQuickReply: (option: string) => void;
  handleMentorClick: (mentorName: string) => Promise<void>;
  handleBookCallRequest: (course: Course) => void;
  handleLeadGateSubmit: (data: {
    fullName: string;
    phone: string;
    selectedMentorCount: number;
    consentGiven: boolean;
    preferredTime?: 'morning' | 'afternoon' | 'evening';
  }) => Promise<void>;
  handleLeadGateSkip: () => void;
  dismissLeadGate: () => void;
  resetChat: () => void;
  setUserInteracted: () => void;
  loadConversationById: (conversationId: string) => Promise<void>;
  startNewConversation: () => Promise<void>;
  handleMessageBlockEmailSubmit: (email: string) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── MAIN HOOK ────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

export function useChat(
  skipWelcome: boolean = false,
  onBookCall?: (course: Course) => void,
  quizContext?: ParsedQuizContext,
  introMessage?: string
): UseChatReturn {
  const fromQuiz = !!quizContext?.trackKey;

  const initialMessages: Message[] = (() => {
    if (fromQuiz && quizContext) {
      const userMsgId = generateUniqueMessageId();
      const introId = generateUniqueMessageId();
      return [
        {
          id: userMsgId,
          role: 'user' as const,
          content: `רוצה לראות קורסים ב${quizContext.trackTitle}`,
          timestamp: Date.now() - 1,
        },
        {
          id: introId,
          role: 'assistant' as const,
          content: buildQuizWarmIntro(quizContext),
          timestamp: Date.now(),
        },
      ];
    }

    if (introMessage) {
      return [
        {
          id: generateUniqueMessageId(),
          role: 'assistant' as const,
          content: introMessage,
          timestamp: Date.now(),
        },
      ];
    }

    return [];
  })();

  const initialSkipTypingIds = new Set<string>(initialMessages.map((m) => m.id));

  const [state, dispatch] = useReducer(chatReducer, {
    ...initialState,
    messages: initialMessages,
    skipTypingIds: initialSkipTypingIds,
    quickReplies: [],
  });

  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const initialMessageProcessed = useRef(false);
  const pendingCoursesRef = useRef<Course[]>([]);
  const leadGateResumeRef = useRef<LeadGateData | null>(null);
  const leadGateSkipProcessedRef = useRef<boolean>(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimeoutFiredRef = useRef(false);
  const quizSearchFiredRef = useRef(false);

  const stateRef = useRef(state);
  stateRef.current = state;

  const createManagedTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = setTimeout(() => {
      callback();
      const index = timeoutsRef.current.indexOf(timeoutId);
      if (index > -1) timeoutsRef.current.splice(index, 1);
    }, delay);
    timeoutsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  useEffect(() => {
    if (!skipWelcome && !fromQuiz && !initialMessageProcessed.current) {
      initialMessageProcessed.current = true;
      void trackChatFlowEvent({ eventType: 'chat_opened' });
    }
  }, [skipWelcome, fromQuiz]);

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, []);

  const loadingConversationIdRef = useRef<string | null>(null);

  const currentConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    const newId = state.currentConversationId;
    if (newId !== currentConversationIdRef.current) {
      currentConversationIdRef.current = newId;
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    }
  }, [state.currentConversationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await conversationService.getCurrentConversationId();
        if (!cancelled) dispatch({ type: 'SET_CONVERSATION_ID', payload: id || null });
      } catch {
        if (!cancelled) dispatch({ type: 'SET_CONVERSATION_ID', payload: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.leadGateData !== null) {
      leadGateSkipProcessedRef.current = false;
    }
  }, [state.leadGateData]);

  const setUserInteracted = useCallback(() => {
    if (!stateRef.current.hasUserInteracted) {
      dispatch({ type: 'SET_USER_INTERACTED', payload: true });
      dispatch({ type: 'SET_AUTO_SCROLL', payload: true });
    }
  }, []);

  // ─── Webhook search ────────────────────────────────────────────────────────

  const handleWebhookSearch = useCallback(async (searchData: SearchData) => {
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current = [];
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    void trackChatFlowEvent({
      eventType: 'search_triggered',
      conversationId: stateRef.current.currentConversationId,
      topic: (searchData as any).topic ?? null,
    });

    dispatch({ type: 'CLEAR_COURSES' });
    dispatch({ type: 'CLEAR_QUICK_REPLIES' });
    dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: null });
    dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
    dispatch({ type: 'SET_SEARCHING', payload: { isSearching: true, message: 'מחפש התאמות...' } });

    searchTimeoutFiredRef.current = false;
    searchTimeoutRef.current = setTimeout(() => {
      if (stateRef.current.isSearching) {
        searchTimeoutFiredRef.current = true;
        dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'החיפוש לוקח יותר מהרגיל ⏳ אפשר לנסות שוב עכשיו, או להשאיר פרטים ואשלח לך התאמות אישיות למייל.',
            timestamp: Date.now(),
          },
        });
        dispatch({ type: 'SET_QUICK_REPLIES', payload: RECOVERY_QUICK_REPLIES });
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
      }
      searchTimeoutRef.current = null;
    }, SEARCH_TIMEOUT_MS);

    try {
      const rawResult = await chatService.sendToWebhook(searchData);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      if (searchTimeoutFiredRef.current) {
        searchTimeoutFiredRef.current = false;
        return;
      }

      const normalized = normalizeWebhookResponse(rawResult);

      let result: WebhookResult;
      try {
        result = validateWebhookResult(normalized);
      } catch (validationError) {
        console.error('[handleWebhookSearch] ❌ Invalid webhook response:', rawResult, validationError);
        dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'נתקלתי בתקלה טכנית בקבלת התוצאות. ננסה שוב או שאשמור פרטים ונחזור אליך עם התאמה מדויקת?',
            timestamp: Date.now(),
          },
        });
        dispatch({ type: 'SET_QUICK_REPLIES', payload: RECOVERY_QUICK_REPLIES });
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
        return;
      }

      dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });

      // Guard: courses array is the single source of truth — show "no results" whenever empty
      if (result.courses.length === 0) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'עדיין לא מצאתי התאמה מספיק טובה. נוכל לדייק יחד את החיפוש, או שתשאירו פרטים ונאתר עבורכם אפשרויות.',
            timestamp: Date.now(),
          },
        });
        dispatch({
          type: 'SET_QUICK_REPLIES',
          payload: ['נדייק את החיפוש', 'חיפוש רחב יותר', 'אני רוצה להשאיר פרטים'],
        });
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
        return;
      }

      dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: searchData });

      let coursesToDisplay = result.courses;

      if (coursesToDisplay.length > 0) {
        const currentDisplayedIds = stateRef.current.displayedCourseIds;
        const newCourses = coursesToDisplay.filter((course) => !currentDisplayedIds.has(course.id));

        if (newCourses.length === 0 && coursesToDisplay.length > 0) {
          dispatch({
            type: 'ADD_MESSAGE',
            payload: {
              id: generateUniqueMessageId(),
              role: 'assistant',
              content: 'אלה כל הקורסים שמצאתי שמתאימים לחיפוש שלך. רוצה לחפש משהו אחר?',
              timestamp: Date.now(),
            },
          });
          dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
          return;
        }

        coursesToDisplay = newCourses;
        pendingCoursesRef.current = newCourses;
      }

      const leadGateData: LeadGateData = {
        // Use the actual number of courses we're displaying; fall back to result.matchCount
        // when courses were returned but all happen to have been deduplicated away.
        matchCount: coursesToDisplay.length > 0 ? coursesToDisplay.length : result.matchCount,
        userSummary: result.userSummary,
        searchData,
        courses: coursesToDisplay,
      };

      if (!shouldSkipLeadGate(searchData)) {
        leadGateResumeRef.current = leadGateData;
      } else {
        leadGateResumeRef.current = null;
      }

      dispatch({ type: 'SET_LEAD_GATE_DATA', payload: leadGateData });

      void trackChatFlowEvent({
        eventType: 'courses_shown',
        conversationId: stateRef.current.currentConversationId,
        coursesCount: coursesToDisplay.length,
        topic: (searchData as any).topic ?? null,
      });

      if (!shouldSkipLeadGate(searchData)) {
        void trackChatFlowEvent({
          eventType: 'lead_gate_shown',
          conversationId: stateRef.current.currentConversationId,
          coursesCount: coursesToDisplay.length,
          topic: (searchData as any).topic ?? null,
        });
      }

      if (shouldSkipLeadGate(searchData)) {
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
      }
    } catch (error) {
      console.error('[handleWebhookSearch] ❌ Error during webhook search:', error);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          id: generateUniqueMessageId(),
          role: 'assistant',
          content: 'מצטער, קרתה שגיאה זמנית בחיפוש. אפשר לנסות שוב מיד או להשאיר פרטים ואחזור אליך עם המלצות.',
          timestamp: Date.now(),
        },
      });
      dispatch({ type: 'SET_QUICK_REPLIES', payload: RECOVERY_QUICK_REPLIES });
      dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
    }
  }, []);

  // ─── Quiz auto-search on mount ─────────────────────────────────────────────

  useEffect(() => {
    if (!fromQuiz || !quizContext || quizSearchFiredRef.current) return;
    quizSearchFiredRef.current = true;

    const searchData = buildQuizSearchData(quizContext);

    const tid = createManagedTimeout(async () => {
      await handleWebhookSearch(searchData as SearchData);
    }, 900);

    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromQuiz]);

  // ─── sendMessage ───────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text: string) => {
      const currentState = stateRef.current;
      const rawText = (text ?? '').toString();
      const decodedText = safeDecodeURIComponent(rawText);

      if (!decodedText || currentState.isLoading || currentState.isSending) return;

      if (decodedText.length > 1000) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'ההודעה ארוכה מדי (מקסימום 1000 תווים). אנא קצר אותה.',
            timestamp: Date.now(),
          },
        });
        return;
      }

      dispatch({ type: 'SET_SENDING', payload: true });

      try {
        const direct = extractSearchDataFromText(decodedText);
        if (direct) {
          setUserInteracted();
          const userMessage: Message = {
            id: generateUniqueMessageId(),
            role: 'user',
            content: decodedText,
            timestamp: Date.now(),
          };
          dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
          dispatch({ type: 'INCREMENT_USER_MESSAGE_COUNT' });
          dispatch({ type: 'CLEAR_QUICK_REPLIES' });

          const preId = generateUniqueMessageId();
          dispatch({
            type: 'ADD_MESSAGE',
            payload: { id: preId, role: 'assistant', content: 'מעולה — מחפש לך קורסים מתאימים...', timestamp: Date.now() },
          });
          dispatch({ type: 'SKIP_TYPING', payload: preId });
          dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: direct as any });
          await handleWebhookSearch(direct as any);
          return;
        }

        if (decodedText.trim() === RESUME_LEAD_GATE_TEXT && leadGateResumeRef.current) {
          dispatch({ type: 'CLEAR_QUICK_REPLIES' });
          dispatch({ type: 'SET_LEAD_GATE_DATA', payload: leadGateResumeRef.current });
          return;
        }

        const isFirstUserMessage = currentState.messages.filter((m) => m.role === 'user').length === 0;
        if (isFirstUserMessage) {
          trackChatStart();
          const conversationId = await conversationService.getCurrentConversationId();
          dispatch({ type: 'SET_CONVERSATION_ID', payload: conversationId || null });
          trackChatSessionStart({
            firstMessage: decodedText,
            sourcePage: fromQuiz ? '/quiz' : '/chat',
            conversationId: conversationId || undefined,
          });
        }

        setUserInteracted();

        const lastAssistantMessage = currentState.messages.filter((m) => m.role === 'assistant').pop();
        if (lastAssistantMessage) dispatch({ type: 'SKIP_TYPING', payload: lastAssistantMessage.id });

        const userMessage: Message = {
          id: generateUniqueMessageId(),
          role: 'user',
          content: decodedText,
          timestamp: Date.now(),
        };
        dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
        dispatch({ type: 'INCREMENT_USER_MESSAGE_COUNT' });
        dispatch({ type: 'CLEAR_QUICK_REPLIES' });

        const userMessageCount = currentState.messages.filter((m) => m.role === 'user').length + 1;
        const isQuickReplyMsg = currentState.quickReplies.includes(decodedText);
        void trackChatFlowEvent({
          eventType: 'user_message',
          conversationId: stateRef.current.currentConversationId,
          stepNumber: userMessageCount,
          isQuickReply: isQuickReplyMsg,
          messagePreview: isQuickReplyMsg ? decodedText : decodedText.slice(0, 200),
        });

        conversationService.saveMessage('user', decodedText).catch((err) => {
          console.error('[sendMessage] ⚠️ Failed to save user message:', err);
        });

        if (decodedText.includes('אני רוצה להוסיף עוד פרטים') && currentState.lastDisplayedCourses.length > 0) {
          dispatch({ type: 'SET_WAITING_FOR_REFINEMENT', payload: true });
          dispatch({
            type: 'ADD_MESSAGE',
            payload: {
              id: generateUniqueMessageId(),
              role: 'assistant',
              content: 'בטח! איזה מידע נוסף תרצו להוסיף שיעזור לי לחפש טוב יותר?',
              timestamp: Date.now(),
            },
          });
          return;
        }

        if (
          decodedText.includes('בשורה התחתונה - מה הכי מתאים לי?') ||
          decodedText.includes('הצג לי את הקורס הרלוונטי ביותר')
        ) {
          const coursesToAnalyze =
            currentState.lastDisplayedCourses.length > 0 ? currentState.lastDisplayedCourses : currentState.courses;

          if (coursesToAnalyze.length === 0) {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: generateUniqueMessageId(),
                role: 'assistant',
                content: 'מצטער, נראה שאין לי קורסים להמליץ עליהם. בוא נחפש שוב?',
                timestamp: Date.now(),
              },
            });
            return;
          }

          if (currentState.lastDisplayedCourses.length === 0 && currentState.courses.length > 0) {
            dispatch({
              type: 'SET_COURSES',
              payload: {
                courses: currentState.courses,
                messageId: currentState.coursesMessageId || generateUniqueMessageId(),
              },
            });
          }

          const analyzingMessageId = generateUniqueMessageId();
          dispatch({
            type: 'ADD_MESSAGE',
            payload: { id: analyzingMessageId, role: 'assistant', content: 'אנתח לך מה הכי מתאים לך מהרשימה...', timestamp: Date.now() },
          });
          dispatch({ type: 'SKIP_TYPING', payload: analyzingMessageId });
          dispatch({ type: 'SET_LOADING', payload: true });

          try {
            const result = await chatService.generateBottomLine(coursesToAnalyze, currentState.lastSearchData);
            const recommendedCourse =
              coursesToAnalyze.find((course) => course.name.trim() === result.courseName.trim()) ||
              coursesToAnalyze.find(
                (course) => course.name.includes(result.courseName) || result.courseName.includes(course.name)
              ) ||
              coursesToAnalyze[0];

            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: recommendedCourse });

            const recommendationCardMessageId = generateUniqueMessageId();
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: recommendationCardMessageId,
                role: 'assistant',
                content: `[BOTTOM_LINE_CARD]${result.recommendation}`,
                timestamp: Date.now(),
              },
            });
            dispatch({ type: 'SKIP_TYPING', payload: recommendationCardMessageId });
            dispatch({ type: 'ADD_SHOWN_INSIGHT', payload: 'bottomLine' });
            createManagedTimeout(() => {
              const s = stateRef.current;
              dispatch({ type: 'SET_QUICK_REPLIES', payload: getSmartQuickReplies(s.shownInsights) });
            }, 100);
          } catch (error) {
            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: generateUniqueMessageId(),
                role: 'assistant',
                content: 'מצטער, נתקלתי בבעיה בניתוח. אבל אתה יכול ללחוץ על "דבר איתי" בכל קורס לקבל מידע נוסף.',
                timestamp: Date.now(),
              },
            });
          }
          return;
        }

        if (decodedText.includes('תסכם לי את ההבדלים')) {
          const coursesToCompare =
            currentState.lastDisplayedCourses.length > 0 ? currentState.lastDisplayedCourses : currentState.courses;

          if (coursesToCompare.length === 0) {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: generateUniqueMessageId(),
                role: 'assistant',
                content: 'מצטער, אין קורסים להשוות. בוא נחפש שוב?',
                timestamp: Date.now(),
              },
            });
            return;
          }

          try {
            if (currentState.lastDisplayedCourses.length === 0 && currentState.courses.length > 0) {
              dispatch({
                type: 'SET_COURSES',
                payload: {
                  courses: currentState.courses,
                  messageId: currentState.coursesMessageId || generateUniqueMessageId(),
                },
              });
            }
            const tableMessageId = generateUniqueMessageId();
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: tableMessageId, role: 'assistant', content: '[COMPARISON_TABLE]', timestamp: Date.now() },
            });
            dispatch({ type: 'SKIP_TYPING', payload: tableMessageId });
            dispatch({ type: 'ADD_SHOWN_INSIGHT', payload: 'comparison' });
            createManagedTimeout(() => {
              const s = stateRef.current;
              dispatch({ type: 'SET_QUICK_REPLIES', payload: getSmartQuickReplies(s.shownInsights) });
            }, 300);
          } catch (error) {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'מצטער, נתקלתי בבעיה בסיכום.', timestamp: Date.now() },
            });
          }
          return;
        }

        if (decodedText.includes('אפשר כיוון אחר?') && currentState.lastDisplayedCourses.length > 0) {
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_WAITING_FOR_REFINEMENT', payload: true });
          const empathyMessage: Message = {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content:
              'אין בעיה, אני כאן לעזור! מה פחות התאים לך בתוצאות האלה?\n\n[OPTIONS]המחיר גבוה מדי|משך הזמן ארוך מדי|אני מעדיף למידה פרונטלית|אני מעדיף למידה מרחוק|נושא אחר לגמרי[/OPTIONS]',
            timestamp: Date.now(),
          };
          dispatch({ type: 'ADD_MESSAGE', payload: empathyMessage });
          const parsed = parseMessage(empathyMessage.content);
          if (parsed.options.length > 0) {
            createManagedTimeout(() => {
              dispatch({ type: 'SET_QUICK_REPLIES', payload: limitOptions(parsed.options) });
            }, 100);
          }
          return;
        }

        if (currentState.waitingForRefinement && currentState.lastSearchData) {
          dispatch({ type: 'SET_WAITING_FOR_REFINEMENT', payload: false });
          dispatch({ type: 'CLEAR_QUICK_REPLIES' });

          let updatedSearchData: any = { ...currentState.lastSearchData };
          let refinementMessage = '';

          if (
            decodedText.includes('המחיר גבוה מדי') ||
            decodedText.toLowerCase().includes('זול') ||
            decodedText.toLowerCase().includes('יקר')
          ) {
            refinementMessage = 'אוקיי, אחפש לך אפשרויות יותר משתלמות...';
            updatedSearchData.budget = 'low';
          } else if (
            decodedText.includes('משך הזמן ארוך מדי') ||
            decodedText.toLowerCase().includes('קצר') ||
            decodedText.toLowerCase().includes('מהיר')
          ) {
            refinementMessage = 'בסדר, אחפש לך קורסים קצרים יותר...';
            updatedSearchData.time = 'short';
          } else if (decodedText.includes('אני מעדיף למידה פרונטלית') || decodedText.toLowerCase().includes('פרונטלי')) {
            refinementMessage = 'מעולה, אחפש לך קורסים פרונטליים...';
            updatedSearchData.learning_type = 'frontal';
          } else if (
            decodedText.includes('אני מעדיף למידה מרחוק') ||
            decodedText.toLowerCase().includes('אונליין') ||
            decodedText.toLowerCase().includes('מרחוק')
          ) {
            refinementMessage = 'בסדר, אחפש לך קורסים מקוונים...';
            updatedSearchData.learning_type = 'online';
          } else if (decodedText.includes('נושא אחר לגמרי')) {
            dispatch({ type: 'CLEAR_COURSES' });
            dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: null });
            dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: null });
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'בטח! אז בוא נתחיל מחדש. מה תרצה ללמוד?', timestamp: Date.now() },
            });
            return;
          } else {
            refinementMessage = 'אוקיי, אחפש לך אפשרויות אחרות...';
          }

          dispatch({
            type: 'ADD_MESSAGE',
            payload: { id: generateUniqueMessageId(), role: 'assistant', content: refinementMessage, timestamp: Date.now() },
          });
          dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: updatedSearchData });
          await handleWebhookSearch(updatedSearchData);
          return;
        }

        const moreCoursesKeywords = [
          'עוד קורסים',
          'קורסים אחרים',
          'קורסים נוספים',
          'יש עוד',
          'תחפש לי עוד',
          'תמצא לי עוד',
          'תראה לי עוד',
          'משהו שונה',
          'משהו אחר',
          'אפשרויות נוספות',
          'תוצאות נוספות',
          'חיפוש נוסף',
        ];

        const isRequestingMoreCourses = moreCoursesKeywords.some((keyword) =>
          decodedText.toLowerCase().includes(keyword.toLowerCase())
        );

        if (isRequestingMoreCourses && currentState.lastSearchData && !currentState.waitingForRefinement) {
          if (currentState.isSearching) return;
          dispatch({ type: 'CLEAR_COURSES' });
          dispatch({ type: 'CLEAR_QUICK_REPLIES' });
          dispatch({
            type: 'ADD_MESSAGE',
            payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'בטח! אני מחפש לך עוד קורסים מתאימים...', timestamp: Date.now() },
          });
          await handleWebhookSearch(currentState.lastSearchData);
          return;
        }

        // ==== AI chat fallback ====
        dispatch({ type: 'SET_LOADING', payload: true });
        if (currentState.messages.length === 1) {
          dispatch({ type: 'SET_PLACEHOLDER', payload: '...כתוב הודעה' });
        }

        try {
          const filteredMessages = [...currentState.messages, userMessage].filter((msg) => {
            const content = msg.content;
            return (
              !content.startsWith('[COMPARISON_TABLE]') &&
              !content.startsWith('[BOTTOM_LINE_CARD]') &&
              !content.includes('[COMPARISON_TABLE]') &&
              !content.includes('[BOTTOM_LINE_CARD]')
            );
          });

          const limitedMessages = filteredMessages.slice(-20);
          const response = await chatService.sendChatMessage(
            limitedMessages,
            currentState.lastDisplayedCourses.length > 0 ? currentState.lastDisplayedCourses : undefined
          );

          if (response.mentorQuery && response.mentorData) {
            if (response.message) {
              const parsed = parseMessage(response.message);
              dispatch({
                type: 'ADD_MESSAGE',
                payload: { id: generateUniqueMessageId(), role: 'assistant', content: parsed.cleanContent, timestamp: Date.now() },
              });
              if (parsed.options.length > 0) {
                const t = setTimeout(() => {
                  dispatch({ type: 'SET_QUICK_REPLIES', payload: limitOptions(parsed.options) });
                }, 100);
                timeoutsRef.current.push(t);
              }
            }
            const mentorSearchData: SearchData = { query_type: 'mentor' as any, mentor_name: response.mentorData.mentor_name as any } as any;
            dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: mentorSearchData });
            await handleWebhookSearch(mentorSearchData);
          } else if (response.searchReady && response.searchData) {
            if (response.message) {
              const parsed = parseMessage(response.message);
              const preSearchContent = parsed.cleanContent.trim();
              if (preSearchContent.length > 0) {
                dispatch({
                  type: 'ADD_MESSAGE',
                  payload: { id: generateUniqueMessageId(), role: 'assistant', content: preSearchContent, timestamp: Date.now() },
                });
              }
            }
            const searchDataToSend: any = { ...response.searchData };
            if (!searchDataToSend.summary || searchDataToSend.summary.trim().length === 0) {
              const parts: string[] = [];
              if (searchDataToSend.topic) parts.push(searchDataToSend.topic);
              if (searchDataToSend.subtopic) parts.push(searchDataToSend.subtopic);
              searchDataToSend.summary = parts.length ? `קורס ${parts.join(' ')}` : 'חיפוש קורסים';
            }
            dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: searchDataToSend });
            await handleWebhookSearch(searchDataToSend);
          } else if (response.message) {
            const parsed = parseMessage(response.message);
            let messageContent = parsed.cleanContent.trim();
            if (messageContent.length === 0 && parsed.options.length > 0) messageContent = 'בחר אפשרות:';
            if (messageContent.length === 0) messageContent = response.message.trim() || 'אני כאן לעזור. איך אפשר לסייע?';

            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: messageContent, timestamp: Date.now() },
            });

            if (parsed.options.length > 0) {
              const stepNum = currentState.messages.filter((m) => m.role === 'user').length + 1;
              void trackChatFlowEvent({
                eventType: 'assistant_question',
                conversationId: stateRef.current.currentConversationId,
                stepNumber: stepNum,
                optionsCount: parsed.options.length,
              });
              const t = setTimeout(() => {
                dispatch({ type: 'SET_QUICK_REPLIES', payload: limitOptions(parsed.options) });
              }, 100);
              timeoutsRef.current.push(t);
            } else {
              dispatch({ type: 'SET_QUICK_REPLIES', payload: [] });
            }
          } else {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'אני כאן לעזור. איך אני יכול לסייע?', timestamp: Date.now() },
            });
          }
        } catch (innerError) {
          dispatch({
            type: 'ADD_MESSAGE',
            payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'נתקלתי בבעיה. ננסה שוב?', timestamp: Date.now() },
          });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } finally {
        dispatch({ type: 'SET_SENDING', payload: false });
      }
    },
    [handleWebhookSearch, onBookCall, setUserInteracted, fromQuiz, createManagedTimeout]
  );

  const handleQuickReply = useCallback(
    (option: string) => {
      void trackButtonClick('chat_quick_reply', '/chat', { reply_text: option });
      sendMessage(option);
    },
    [sendMessage]
  );

  const handleMentorClick = useCallback(
    async (mentorName: string) => {
      const currentState = stateRef.current;
      setUserInteracted();

      const userMessage: Message = {
        id: generateUniqueMessageId(),
        role: 'user',
        content: `ספר לי על ${mentorName}`,
        timestamp: Date.now(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const limitedMessages = [...currentState.messages, userMessage].slice(-20);
        const response = await chatService.sendChatMessage(limitedMessages);

        if (response.mentorQuery && response.mentorData) {
          if (response.message) {
            const parsed = parseMessage(response.message);
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: parsed.cleanContent, timestamp: Date.now() },
            });
            if (parsed.options.length > 0) {
              const t = setTimeout(() => {
                dispatch({ type: 'SET_QUICK_REPLIES', payload: limitOptions(parsed.options) });
              }, 100);
              timeoutsRef.current.push(t);
            }
          }
          const mentorSearchData: SearchData = { query_type: 'mentor' as any, mentor_name: response.mentorData.mentor_name as any } as any;
          dispatch({ type: 'SET_LAST_SEARCH_DATA', payload: mentorSearchData });
          await handleWebhookSearch(mentorSearchData);
        } else if (response.message) {
          const parsed = parseMessage(response.message);
          const messageContent = parsed.cleanContent.trim();
          if (messageContent.length > 0) {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: { id: generateUniqueMessageId(), role: 'assistant', content: messageContent, timestamp: Date.now() },
            });
          }
          if (parsed.options.length > 0) {
            const t = setTimeout(() => {
              dispatch({ type: 'SET_QUICK_REPLIES', payload: limitOptions(parsed.options) });
            }, 100);
            timeoutsRef.current.push(t);
          } else {
            dispatch({ type: 'SET_QUICK_REPLIES', payload: [] });
          }
        }
      } catch (error) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'נתקלתי בבעיה. ננסה שוב?', timestamp: Date.now() },
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [handleWebhookSearch, setUserInteracted]
  );

  const resetChat = useCallback(() => {
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current = [];
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    dispatch({ type: 'CLEAR_COURSES' });
    dispatch({ type: 'CLEAR_QUICK_REPLIES' });
    dispatch({ type: 'SET_LOADING', payload: false });
    dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
    dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: null });
    dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
    dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
  }, []);

  const handleBookCallRequest = useCallback(
    (course: Course) => {
      if (onBookCall) onBookCall(course);
    },
    [onBookCall]
  );

  const loadConversationById = useCallback(async (conversationId: string) => {
    try {
      loadingConversationIdRef.current = conversationId;

      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      dispatch({ type: 'CLEAR_COURSES' });
      dispatch({ type: 'CLEAR_QUICK_REPLIES' });
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
      dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: null });
      dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
      dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
      dispatch({ type: 'SET_CONVERSATION_ID', payload: conversationId });

      const messages = await conversationService.loadConversationById(conversationId);

      if (loadingConversationIdRef.current !== conversationId) return;

      if (messages.length > 0) {
        dispatch({ type: 'SET_MESSAGES', payload: messages });
        messages.forEach((msg) => dispatch({ type: 'SKIP_TYPING', payload: msg.id }));
      }
    } catch (error) {
      console.error('[useChat] ❌ Error loading conversation:', error);
    }
  }, []);

  const startNewConversation = useCallback(async () => {
    try {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      conversationService.clearCurrentConversation();
      await conversationService.createNewConversation();
      const newId = await conversationService.getCurrentConversationId().catch(() => null);
      dispatch({ type: 'SET_CONVERSATION_ID', payload: newId || null });
      dispatch({ type: 'CLEAR_COURSES' });
      dispatch({ type: 'CLEAR_QUICK_REPLIES' });
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_SEARCHING', payload: { isSearching: false, message: null } });
      dispatch({ type: 'SET_RECOMMENDED_COURSE', payload: null });
      dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
      dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });

      // No welcome message
    } catch (error) {
      console.error('[useChat] ❌ Error starting new conversation:', error);
    }
  }, [skipWelcome, fromQuiz]);

  const handleLeadGateSubmit = useCallback(
    async (data: {
      fullName: string;
      phone: string;
      selectedMentorCount: number;
      consentGiven: boolean;
      preferredTime?: 'morning' | 'afternoon' | 'evening';
    }) => {
      const currentState = stateRef.current;
      if (!currentState.leadGateData) throw new Error('No lead gate data available');

      const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
      const supabaseKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) throw new Error('Missing Supabase configuration');

      const payload = {
        fullName: data.fullName,
        phone: data.phone,
        selectedMentorCount: data.selectedMentorCount,
        matchCount: currentState.leadGateData.matchCount,
        userSummary: currentState.leadGateData.userSummary,
        searchData: currentState.leadGateData.searchData,
        consentGiven: data.consentGiven,
        preferredTime: data.preferredTime,
      };

      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke('submit-course-match-lead', {
          body: payload,
        });
        if (fnError) throw new Error(fnError.message || 'Failed to submit lead');
        if (!fnData?.success) throw new Error(fnData?.error || 'Failed to submit lead');

        void trackChatFlowEvent({
          eventType: 'lead_submitted',
          conversationId: stateRef.current.currentConversationId,
          coursesCount: currentState.leadGateData?.matchCount ?? null,
          topic: (currentState.leadGateData?.searchData as any)?.topic ?? null,
        });
        dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
        dispatch({ type: 'CLEAR_QUICK_REPLIES' });
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'מצוין! קיבלנו את הפרטים שלך והעברנו אותם.',
            timestamp: Date.now(),
          },
        });
        pendingCoursesRef.current = [];
        return;
      } catch (invokeErr) {
        const url = `${normalizeSupabaseUrl(supabaseUrl)}/functions/v1/submit-course-match-lead`;
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseKey}`,
            apikey: supabaseKey,
          },
          body: JSON.stringify(payload),
        });

        const text = await resp.text().catch(() => '');
        const parsed = text ? safeParseJson<any>(text) : null;

        if (!resp.ok) {
          const details = parsed?.message || parsed?.details || parsed?.error || text || resp.statusText;
          throw new Error(`Failed to submit lead (${resp.status}): ${details}`);
        }
        if (parsed && parsed.success === false) throw new Error(parsed?.message || parsed?.error || 'Failed to submit lead');

        dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
        dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
        dispatch({ type: 'CLEAR_QUICK_REPLIES' });
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: generateUniqueMessageId(),
            role: 'assistant',
            content: 'מצוין! העברנו את הפרטים לבעלי הקורסים.',
            timestamp: Date.now(),
          },
        });
        pendingCoursesRef.current = [];
      }
    },
    []
  );

  const handleLeadGateSkip = useCallback(() => {
    if (leadGateSkipProcessedRef.current) return;
    leadGateSkipProcessedRef.current = true;
    void trackChatFlowEvent({
      eventType: 'lead_gate_skipped',
      conversationId: stateRef.current.currentConversationId,
    });
    dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
    dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });

    const currentState = stateRef.current;
    const lastMessage = currentState.messages[currentState.messages.length - 1];
    if (!lastMessage || lastMessage.content !== 'איך אפשר לעזור לך?') {
      dispatch({
        type: 'ADD_MESSAGE',
        payload: { id: generateUniqueMessageId(), role: 'assistant', content: 'איך אפשר לעזור לך?', timestamp: Date.now() },
      });
    }
    if (leadGateResumeRef.current) {
      dispatch({ type: 'SET_QUICK_REPLIES', payload: [RESUME_LEAD_GATE_TEXT] });
    } else {
      dispatch({ type: 'SET_QUICK_REPLIES', payload: [] });
    }
  }, []);

  const dismissLeadGate = useCallback(() => {
    leadGateSkipProcessedRef.current = false;
    dispatch({ type: 'SET_LEAD_GATE_DATA', payload: null });
    dispatch({ type: 'SET_LEAD_GATE_LOCKED', payload: false });
  }, []);

  const handleMessageBlockEmailSubmit = useCallback(async (email: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();

      const conversationId = await conversationService.getCurrentConversationId();

      const { error } = await supabase.from('email_summary_requests').insert({
        email: normalizedEmail,
        conversation_id: conversationId || null,
        request_source: 'chat_message_limit',
        consent_given: true,
      });

      if (error) {
        console.error('[handleMessageBlockEmailSubmit] Error saving email:', error);
        throw error;
      }

      dispatch({ type: 'SET_MESSAGE_BLOCK_EMAIL_CAPTURED', payload: true });

      const thankYouMessage: Message = {
        id: generateUniqueMessageId(),
        role: 'assistant',
        content: 'תודה רבה! עכשיו אפשר להמשיך לשוחח 😊',
        timestamp: Date.now(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: thankYouMessage });
      dispatch({ type: 'SKIP_TYPING', payload: thankYouMessage.id });

      trackButtonClick('message_block_email_submitted', '/chat', {
        conversationId: conversationId || undefined,
      });
    } catch (err) {
      console.error('[handleMessageBlockEmailSubmit] Failed:', err);
      throw err;
    }
  }, []);

  return {
    state,
    dispatch,
    sendMessage,
    handleQuickReply,
    handleMentorClick,
    handleBookCallRequest,
    handleLeadGateSubmit,
    handleLeadGateSkip,
    dismissLeadGate,
    resetChat,
    setUserInteracted,
    loadConversationById,
    startNewConversation,
    handleMessageBlockEmailSubmit,
  };
}
